import { Type } from '@angular/core';
import { property } from '@arcgis/core/core/accessorSupport/decorators';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import type EsriMap from '@arcgis/core/Map';
import type { ThemeColor } from '../../jmkw/theme-color.type';
import type { JepBasemap, JepLayer } from './jep-arcgis.types';
import { styleBatchRevision } from './style-visibility-batch';
import type { ResourceHandle } from "@arcgis/core/core/Handles";

/** Extended Map interface with theme-aware basemap */
export interface ThemeAwareMap extends EsriMap {
  basemap: JepBasemap & { theme?: ThemeColor };
}

/** Interface for theme-aware layers */
export type ThemeSyncMode = 'basemap' | 'parent' | 'none';

// Global metrics (instrumentation) kept tiny & framework-agnostic.
export interface ThemeMetrics {
  applyThemeCalls: number;
  applyThemeDurations: number[]; // ms durations (capped)
  fallbackPush: number; // total first-use (and late-add) fallbacks
  parentWatcherApply: number;
  basemapWatcherApply: number;
  basemapFlips: {
    layer: string;
    prev: ThemeColor;
    next: ThemeColor;
    ts: number;
  }[];
}
interface ThemeRuntime {
  currentTheme?: ThemeColor;
  metrics: ThemeMetrics;
  flags: {
    enableParentFallbackPush: boolean;
    debug?: boolean;
  };
}

function getThemeRuntime(): ThemeRuntime {
  const root: any = globalThis as any;
  if (!root.__jmkwTheme) {
    root.__jmkwTheme = {
      metrics: {
        applyThemeCalls: 0,
        applyThemeDurations: [],
        fallbackPush: 0,
        parentWatcherApply: 0,
        basemapWatcherApply: 0,
        basemapFlips: []
      } as ThemeMetrics,
      flags: { enableParentFallbackPush: true }
    } as ThemeRuntime;
  }
  return root.__jmkwTheme as ThemeRuntime;
}

function recordThemeMetric(mutator: (metrics: ThemeMetrics) => void): void {
  try {
    mutator(getThemeRuntime().metrics);
  } catch {
    /* noop */
  }
}

export interface ThemeAwareLayerInterface {
  currentTheme: ThemeColor;
  themeSyncMode: ThemeSyncMode;
  onThemeChange?(oldTheme: ThemeColor, newTheme: ThemeColor): void;
  applyTheme(theme: ThemeColor): void;
}

export function setThemeRuntimeCurrentTheme(theme?: ThemeColor): void {
  const runtime = getThemeRuntime();
  if (theme) runtime.currentTheme = theme;
  else delete runtime.currentTheme;
}

export function getThemeRuntimeCurrentTheme(): ThemeColor | undefined {
  return getThemeRuntime().currentTheme;
}

interface ThemeAwareConstructorOptions {
  initialTheme?: ThemeColor;
  themeSyncMode?: ThemeSyncMode;
}

export function ThemeAwareMixin<TBase extends Type<JepLayer>>(
  Base: TBase
): TBase & Type<ThemeAwareLayerInterface> {
  class ThemeAwareLayer extends Base implements ThemeAwareLayerInterface {
    static get enableParentFallbackPush(): boolean {
      return getThemeRuntime().flags.enableParentFallbackPush;
    }
    static setParentFallbackEnabled(val: boolean): void {
      getThemeRuntime().flags.enableParentFallbackPush = val;
    }
    static configureThemeSystem(opts: { debug?: boolean }): void {
      if (typeof opts.debug === 'boolean')
        getThemeRuntime().flags.debug = opts.debug;
    }
    static dumpThemeHealth(opts?: { reset?: boolean }): ThemeMetrics {
      const snapshotBase = getThemeRuntime().metrics;
      const snapshot: ThemeMetrics = {
        ...snapshotBase,
        applyThemeDurations: [...snapshotBase.applyThemeDurations]
      };
      if (opts?.reset) snapshotBase.applyThemeDurations.length = 0;
      return snapshot;
    }
    static resetThemeMetrics(): void {
      const runtime = getThemeRuntime();
      runtime.metrics.applyThemeCalls = 0;
      runtime.metrics.applyThemeDurations.length = 0;
      runtime.metrics.fallbackPush = 0;
      runtime.metrics.parentWatcherApply = 0;
      runtime.metrics.basemapWatcherApply = 0;
      runtime.metrics.basemapFlips.length = 0;
      const root: any = globalThis as any;
      delete root.__themeFirstApplyTs;
    }
    static getMetrics(): ThemeMetrics {
      return this.dumpThemeHealth();
    }
    private static _debug(...args: any[]): void {
      if (getThemeRuntime().flags.debug) console.debug(...args);
    }
    declare map?: ThemeAwareMap;

    private _refreshHandle?: number;
    private _initialized = false;

    @property() currentTheme: ThemeColor = 'light';
    @property() themeSyncMode: ThemeSyncMode = 'basemap';

    onThemeChange?(oldTheme: ThemeColor, newTheme: ThemeColor): void;

    constructor(...args: any[]) {
      super(...args);
      this._applyThemeConstructorOptions(args[0]);
      // Legacy flag removed; callers should set themeSyncMode explicitly
      if (this.themeSyncMode === 'basemap') this._initThemeWatcher();
      this._initParentThemeWatcher();
      this._initChildAddWatcher();
    }

    private _applyThemeConstructorOptions(
      options?: ThemeAwareConstructorOptions
    ): void {
      if (options?.initialTheme) this.currentTheme = options.initialTheme;
      if (options?.themeSyncMode) this.themeSyncMode = options.themeSyncMode;
    }

    /** Find the map by walking parents (handles group layers) */
    private _findMap(): ThemeAwareMap | null {
      const directMap = (this as any).map as ThemeAwareMap | undefined;
      if (directMap?.basemap) return directMap;
      const parentLayerMap = (this as any).parentLayer?.map as
        | ThemeAwareMap
        | undefined;
      if (parentLayerMap?.basemap) return parentLayerMap;
      let current: any = (this as any).parent;
      while (current) {
        if (current.basemap) return current as ThemeAwareMap;
        if (current.map?.basemap) return current.map as ThemeAwareMap;
        current = current.parent;
      }
      return null;
    }

    private _initThemeWatcher(): void {
      if (this._initialized) return;
      this._initialized = true;
      const predicate = () => {
        if (this.themeSyncMode === 'parent') {
          return Boolean(
            this._resolveParentTheme() ||
              getThemeRuntimeCurrentTheme() ||
              this._findMap()
          );
        }
        return Boolean(
          (this._findMap() || getThemeRuntimeCurrentTheme()) && this.loaded
        );
      };
      const onReady = () => {
        const mapRef = this._findMap();
        if (!mapRef && !getThemeRuntimeCurrentTheme()) return;
        const themeIHandle = reactiveUtils.watch(
          () => this._resolveSyncTheme(mapRef),
          (theme?: ThemeColor) => this._handleBasemapTheme(theme),
          { initial: true }
        );
        this.addHandles(themeIHandle, 'theme');
      };
      const handle = reactiveUtils.when(predicate, onReady);
      this.addHandles(handle, 'theme');
    }

    private _resolveSyncTheme(mapRef?: ThemeAwareMap | null): ThemeColor | undefined {
      switch (this.themeSyncMode) {
        case 'basemap':
          return getThemeRuntimeCurrentTheme() ?? mapRef?.basemap?.theme;
        case 'parent': {
          return (
            this._resolveParentTheme() ??
            getThemeRuntimeCurrentTheme() ??
            mapRef?.basemap?.theme
          );
        }
        default:
          return undefined; // 'none'
      }
    }

    private _resolveParentTheme(): ThemeColor | undefined {
      const parent: any = (this as any).parent;
      const parentLayer: any = (this as any).parentLayer;
      return parent?.currentTheme ?? parentLayer?.currentTheme;
    }

    applyTheme(theme: ThemeColor): void {
      if (theme === this.currentTheme) return;
      const prev = this.currentTheme;
      const startTime = (globalThis as any).performance?.now?.() ?? Date.now();
      this.currentTheme = theme;
      const batchRevision = styleBatchRevision(this);
      this.updateLayerStyles(theme);
      this.propagateThemeToChildren(this, theme);
      if (this.visible && prev !== theme) this._scheduleRefresh(batchRevision);
      const elapsedMs =
        ((globalThis as any).performance?.now?.() ?? Date.now()) - startTime;
      recordThemeMetric((metrics) => {
        metrics.applyThemeCalls++;
        metrics.applyThemeDurations.push(elapsedMs);
        // Cap to last 200 samples to avoid unbounded growth
        if (metrics.applyThemeDurations.length > 200)
          metrics.applyThemeDurations.shift();
      });
      this._markFirstApplyTimestamp();
    }

    // Hook for subclasses
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected updateLayerStyles(theme: ThemeColor): void {
      const self: any = this as any;
      if (typeof self.updateVectorStyles === 'function') {
        self.updateVectorStyles(theme, this.visible);
        return;
      }
      if (typeof self.updateStyles === 'function') {
        self.updateStyles(theme);
      }
    }

    protected propagateThemeToChildren(currentLayer: any, theme: ThemeColor): void {
      const children = currentLayer.layers || currentLayer.subLayers?.();
      if (!children?.forEach) return; // nothing to do
      const topLevelParent = this.themeSyncMode !== 'parent';
      for (const child of children) {
        this._propagateThemeToChild(child, theme, topLevelParent);
        this.propagateThemeToChildren(child, theme);
      }
    }

    /** Extracted child propagation to reduce cognitive complexity */
    private _propagateThemeToChild(
      child: any,
      theme: ThemeColor,
      topLevelParent: boolean
    ): void {
      const syncMode: ThemeSyncMode | undefined = child?.themeSyncMode;
      if (!syncMode || syncMode === 'none') return;
      if (syncMode === 'parent' || syncMode === 'basemap') {
        this._maybeFallbackFirstUse(child, theme, topLevelParent);
      } else if (typeof child?.applyTheme === 'function')
        child.applyTheme(theme);
      else if (child && 'currentTheme' in child) child.currentTheme = theme;
    }

    private _maybeFallbackFirstUse(
      child: any,
      theme: ThemeColor,
      topLevelParent: boolean
    ): void {
      if (!(ThemeAwareLayer.enableParentFallbackPush && topLevelParent)) return;
      const firstApply = !child._initialThemeApplied;
      const needsTheme = child.currentTheme !== theme;
      if (!firstApply && !needsTheme) return;
      if (typeof child.applyTheme !== 'function') return;
      try {
        recordThemeMetric((metrics) => {
          metrics.fallbackPush++;
        });
        child.applyTheme(theme);
        child._initialThemeApplied = true;
      } catch (error) {
        ThemeAwareLayer._debug(
          '[ThemeAwareMixin] fallback child theme apply failed',
          error
        );
      }
    }

    // Late-add detection
    private _childAddHandle?: ResourceHandle;
    private _initChildAddWatcher(): void {
      // Only top-level (non-parent sync) layers drive fallback pushes for children
      if (this.themeSyncMode === 'parent') return;
      // Defer until layer is loaded; calling custom getters (e.g. navigation layer) too early caused subLayers() errors
      if (!this.loaded) {
        const once: any = (reactiveUtils as any).whenOnce;
        if (typeof once === 'function') {
          once(() => this.loaded).then(() => this._initChildAddWatcher());
        } else {
          // Fallback for ArcGIS versions lacking whenOnce; when triggers immediately after predicate becomes truthy
          reactiveUtils.when(
            () => this.loaded,
            () => this._initChildAddWatcher()
          );
        }
        return;
      }
      if (this._childAddHandle) return; // already watching
      // Use safe accessor that never throws if underlying implementation not ready
      const safeLength = () => {
        try {
          const anySelf: any = this as any;
          // Some layer implementations expose an array-like .layers, others a getter bridging to subLayers()/sublayers
          const childLayers = anySelf.layers;
          if (Array.isArray(childLayers)) return childLayers.length;
          if (childLayers && typeof childLayers.length === 'number')
            return childLayers.length;
        } catch {
          return -1; // sentinel for not-ready
        }
        return 0;
      };
      this._childAddHandle = reactiveUtils.watch(
        () => safeLength(),
        () => this._handleLateChildren(),
        { initial: true }
      );
      this.addHandles(this._childAddHandle, 'theme');
    }
    private _handleLateChildren(): void {
      if (!ThemeAwareLayer.enableParentFallbackPush) return;
      let children: any;
      try {
        const anySelf: any = this as any;
        children = anySelf.layers;
      } catch {
        return; // not ready yet
      }
      if (!children?.forEach) return; // array-like not ready
      const theme = this.currentTheme;
      children.forEach((child: any) => {
        if (
          child?.themeSyncMode === 'parent' &&
          !child._initialThemeApplied &&
          child.currentTheme !== theme &&
          typeof child.applyTheme === 'function'
        ) {
          try {
            recordThemeMetric((metrics) => {
              metrics.fallbackPush++;
            });
            child.applyTheme(theme);
            child._initialThemeApplied = true;
          } catch (error) {
            ThemeAwareLayer._debug(
              '[ThemeAwareMixin] fallback late-add child theme apply failed',
              error
            );
          }
        }
      });
    }

    /** Watch parent.currentTheme for parent-synced layers (child pull model) */
    private _parentThemeHandle?: ResourceHandle;
    private _initParentThemeWatcher(): void {
      if (this.themeSyncMode !== 'parent' || this._parentThemeHandle) return;
      // Use reactiveUtils to watch parent.currentTheme even before map loaded.
      this._parentThemeHandle = reactiveUtils.watch(
        () =>
          this._resolveParentTheme() ||
          getThemeRuntimeCurrentTheme() ||
          this._findMap()?.basemap?.theme,
        (parentTheme?: ThemeColor) => {
          if (!parentTheme) return; // still nothing to sync
          const self: any = this as any;
          const first = !self._initialThemeApplied;
          if (parentTheme === this.currentTheme) {
            if (first) {
              // Force an initial styling pass even though theme value is identical
              this.updateLayerStyles(parentTheme);
              this.onThemeChange?.(this.currentTheme, parentTheme);
              self._initialThemeApplied = true;
            }
            return;
          }
          const prev = this.currentTheme;
          recordThemeMetric((metrics) => {
            metrics.parentWatcherApply++;
          });
          this.applyTheme(parentTheme); // applyTheme triggers updateLayerStyles + propagation + metrics
          this.onThemeChange?.(prev, parentTheme);
          self._initialThemeApplied = true;
        },
        { initial: true }
      );
      this.addHandles(this._parentThemeHandle, 'theme');
    }

    /** Handle theme changes from basemap watcher (extracted to reduce nesting depth) */
    private _handleBasemapTheme(theme?: ThemeColor): void {
      if (!theme || theme === this.currentTheme) return;
      const prev = this.currentTheme;
      recordThemeMetric((metrics) => {
        metrics.basemapWatcherApply++;
        metrics.basemapFlips.push({
          layer: (this as any).id || 'unnamed',
          prev,
          next: theme,
          ts: Date.now()
        });
        if (metrics.basemapFlips.length > 100) metrics.basemapFlips.shift();
      });
      this.applyTheme(theme);
      this.onThemeChange?.(prev, theme);
    }

    private _markFirstApplyTimestamp(): void {
      const globalCtx: any = globalThis as any;
      if (!globalCtx.__themeFirstApplyTs)
        globalCtx.__themeFirstApplyTs =
          (globalThis as any).performance?.now?.() ?? Date.now();
    }
    // Auto-disable heuristic removed: explicit opting out is clearer for plugin authors

    /**
     * True when this theme change's styling was queued through
     * `queueStyleLayerVisibility()`, measured against a revision sampled before styling ran.
     *
     * Such a layer is restyled by a single `loadStyle()`, which re-fetches its tiles on its
     * own. The `refresh()` that would follow only cancels those in-flight requests, and
     * ArcGIS logs one "error refreshing vector-tiles layer-view" AbortError per cancellation
     * (JMKW-1868).
     *
     * Keyed on the revision rather than on a capability check because the styling may be
     * queued by the layer itself (cultural), by its pseudo-layer children during propagation
     * (navigation), or by a plugin service reacting to the same basemap change (obstacles) —
     * these look nothing alike from here. A layer styled any other way sees no revision
     * change and still refreshes, so the fallback is the pre-existing behavior.
     */
    private _styleWasBatched(revisionBefore: number): boolean {
      return styleBatchRevision(this) !== revisionBefore;
    }

    /**
     * The batch check deliberately happens inside the frame rather than at call time: it is
     * the last point before `refresh()` would run, so it also catches styling queued after
     * `applyTheme()` returns — e.g. by a service subscribed to the same basemap change.
     * Queued batches flush on a microtask, which is always before this frame.
     */
    private _scheduleRefresh(batchRevisionBefore: number): void {
      if (this._refreshHandle) cancelAnimationFrame(this._refreshHandle);
      this._refreshHandle = requestAnimationFrame(() => {
        if (!this._styleWasBatched(batchRevisionBefore)) this._performRefresh();
        this._refreshHandle = undefined;
      });
    }

    private _performRefresh(): void {
      const self: any = this as any;
      if (typeof self.refresh === 'function') self.refresh();
      else if (typeof self.redraw === 'function') self.redraw();
    }

    override destroy(): void {
      if (this._refreshHandle) cancelAnimationFrame(this._refreshHandle);
      this.removeHandles('theme'); // now actually removes our watchers
      super.destroy();
    }
  }

  return ThemeAwareLayer as TBase & Type<ThemeAwareLayerInterface>;
}

export function isThemeAwareLayer(
  layer: unknown
): layer is ThemeAwareLayerInterface {
  return (
    !!layer && typeof layer === 'object' && 'currentTheme' in (layer as any)
  );
}

export function syncLayerWithBasemap(
  layer: ThemeAwareLayerInterface & { map?: ThemeAwareMap; parent?: any },
  map?: EsriMap
): void {
  const findMap = (): ThemeAwareMap | undefined => {
    const anyLayer = layer as any;
    if (anyLayer.map?.basemap) return anyLayer.map as ThemeAwareMap;
    // Pseudo-layers attach via parentLayer rather than parent, so walking only
    // parent would miss their map entirely. Mirrors ThemeAwareMixin._findMap().
    if (anyLayer.parentLayer?.map?.basemap) {
      return anyLayer.parentLayer.map as ThemeAwareMap;
    }
    let current = anyLayer.parent ?? anyLayer.parentLayer;
    while (current) {
      if (current.basemap) return current as ThemeAwareMap;
      if (current.map?.basemap) return current.map as ThemeAwareMap;
      current = current.parent ?? current.parentLayer;
    }
    return undefined;
  };

  const targetMap = (map as ThemeAwareMap | undefined) ?? findMap();
  const theme = getThemeRuntimeCurrentTheme() ?? targetMap?.basemap?.theme;
  if (theme) layer.applyTheme(theme);
}
